import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Copy, CheckCircle2 } from 'lucide-react';
import { useCourse } from '@/contexts/CourseContext';
import { useToast } from '@/hooks/use-toast';
import { capitalizeFirst } from '@/lib/utils';

export default function ProgressBar() {
  const { t } = useTranslation();
  const { currentStep, dispatch, course } = useCourse();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const steps = [
    { number: 1, label: t('progressBar.courseInfo'), checkpoint: t('progressBar.checkpointA') },
    { number: 2, label: t('progressBar.learningObjectives'), checkpoint: t('progressBar.checkpointB') },
    { number: 3, label: t('progressBar.activities'), checkpoint: t('progressBar.checkpointC') },
    { number: 4, label: t('progressBar.finish'), checkpoint: null }
  ];

  const goToStep = (stepNumber) => {
    // Only allow clicking on the current step (no going back to previous steps)
    if (stepNumber === currentStep) {
      dispatch({ type: 'SET_STEP', payload: stepNumber });
    }
  };

  const handleCopyToClipboard = async () => {
    if (!course) return;

    let text = '';
    if (course.title) text += `${course.title}\n`;
    if (course.subjectArea) text += `${t('progressBar.subject')}: ${course.subjectArea}\n`;
    if (course.educationalLevel) text += `${t('progressBar.level')}: ${course.educationalLevel}\n`;
    if (course.grade) text += `${t('progressBar.grade')}: ${course.grade}\n`;
    if (course.totalHours > 0) text += `${t('progressBar.duration')}: ${course.totalHours} ${t('progressBar.hours')}\n`;
    if (course.description) text += `\n${course.description}\n\n`;

    if (course.learningObjectives && course.learningObjectives.length > 0) {
      text += `${t('progressBar.learningObjectives').toUpperCase()}\n`;
      course.learningObjectives.forEach((objective, index) => {
        text += `${index + 1}. ${objective.text}\n`;
      });
    }

    if (course.topics && course.topics.length > 0) {
      text += `\n${t('step4.topicsActivities').toUpperCase()}\n`;
      course.topics.forEach(topic => {
        text += `\n${topic.title}\n`;
        topic.activities.forEach(activity => {
          // Support both new (objectiveId) and legacy (goalId) field names
          const activityObjectiveId = activity.objectiveId || activity.goalId;
          const objectiveText = course.learningObjectives?.find(obj => obj.id === activityObjectiveId)?.text || t('step2.objective');
          text += `  • ${activity.title}\n`;
          text += `    ${t('step2.objective')}: ${objectiveText}\n`;
          text += `    ${activity.description}\n`;
          text += `    ${t('step3.actor')}: ${capitalizeFirst(activity.actor)} | ${t('step3.resource')}: ${activity.resourceType}\n`;
          text += `    ${activity.resourceDescription}\n\n`;
        });
      });
    }

    if (!text.trim()) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: t('common.copied'),
        description: t('common.courseOutlineCopied')
      });
    } catch (error) {
      toast({
        title: t('common.copyFailed'),
        description: t('common.unableToAccessClipboard'),
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="w-full py-3 px-0 mb-0 flex justify-center" data-testid="progress-bar">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-center gap-12 flex-nowrap">
          <div className="flex items-center justify-center relative">
            {steps.map((step, index) => {
            const isCompleted = step.number < currentStep;
            const isCurrent = step.number === currentStep;
            const isAccessible = step.number === currentStep; // Only current step is clickable

            return (
              <div key={step.number} className="flex items-center">
                <div className="flex flex-col items-center relative z-10">
                  <button
                    onClick={() => goToStep(step.number)}
                    disabled={!isAccessible}
                    data-testid={`step-${step.number}-button`}
                    className={`w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 flex items-center justify-center ${
                      isCurrent
                        ? 'bg-gradient-to-br from-orange-500 to-pink-500 text-white shadow-xl scale-110 cursor-default'
                        : isCompleted
                        ? 'bg-teal-500 text-white shadow-lg cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isCompleted ? '✓' : step.number}
                  </button>
                  <span
                    className={`mt-2 text-base font-semibold ${
                      isCurrent ? 'text-orange-700' : isCompleted ? 'text-teal-700' : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="w-32 h-1.5 mx-8 relative top-[-20px]">
                    <div className="h-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          isCompleted ? 'bg-gradient-to-r from-teal-400 to-teal-500 w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
